import React, { type ComponentProps, forwardRef } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { ScrollView, type ScrollViewInstance, StyleSheet } from 'react-native';

import { Aside } from '@expo/html-elements';
import { theme } from '@garnish/constants';
import { useResponsive } from '@garnish/core';

import { useA11ySelector } from '@sg/a11y-helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsContainer = forwardRef<
  ScrollViewInstance,
  CustomizationDetailsContainerProps
>((props, ref) => {
  const { children, isFluid = false, ...restProps } = props;

  const { currentBreakpoint } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomizationDetailsContainerAside isFluid={isFluid}>
      <CustomizationDetailsContainerScrollView ref={ref} {...restProps}>
        {children}
      </CustomizationDetailsContainerScrollView>

      {currentBreakpoint.isXS ? (
        <LinearGradient
          pointerEvents="none"
          locations={SHADOW_GRADIENT_LOCATIONS}
          colors={SHADOW_GRADIENT_COLORS}
          style={styles.shadowGradient}
        />
      ) : null}
    </CustomizationDetailsContainerAside>
  );
});

const CustomizationDetailsContainerAside = (
  props: Pick<CustomizationDetailsContainerProps, 'children' | 'isFluid'>,
) => {
  const { children, isFluid = false } = props;

  const a11ySelector = useA11ySelector('customization.aside-product-configuration'); // prettier-ignore
  const { match } = useResponsive();

  // ─── Styles ──────────────────────────────────────────────────────────

  const outerContainerStyles = [
    match([
      isFluid ? styles.outerContainerFluidXS : undefined,
      styles.outerContainerSM,
      styles.outerContainerLG,
    ]),
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Aside style={outerContainerStyles} {...a11ySelector.props}>
      {children}
    </Aside>
  );
};

const CustomizationDetailsContainerScrollView = forwardRef<
  ScrollViewInstance,
  CustomizationDetailsContainerProps
>((props, ref) => {
  const { children, style, contentContainerStyle, ...restProps } = props;

  const { currentBreakpoint, match } = useResponsive();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [styles.container, match([undefined, styles.containerSM]), style];

  const contentContainerStyles = [styles.contentContainer, contentContainerStyle];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScrollView
      ref={ref}
      style={containerStyles}
      contentContainerStyle={contentContainerStyles}
      // NOTE: The sidebar content is not inset by the bottom safe area, so its
      //       scroll indicator should reach the bottom edge as well.
      automaticallyAdjustsScrollIndicatorInsets={currentBreakpoint.isXS}
      {...restProps}
    >
      {children}
    </ScrollView>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const SHADOW_GRADIENT_LOCATIONS: readonly [number, number, ...number[]] | null = [0, 1];

const SHADOW_GRADIENT_COLORS: readonly [string, string, ...string[]] = [
  theme.colors.OPACITY.OATMEAL.TRANSPARENT,
  theme.colors.OATMEAL,
];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainerFluidXS: {
    flex: 1,
  },
  outerContainerSM: {
    flex: 1,
    width: '100%',
    maxWidth: 350,
  },
  outerContainerLG: {
    width: '100%',
    maxWidth: 400,
  },
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.CREAM,
  },
  containerSM: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
  },
  contentContainer: {
    flexGrow: 1,
  },
  shadowGradient: {
    position: 'absolute',
    right: 0,
    left: 0,
    bottom: 0,
    height: 60,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationDetailsContainerProps = {
  isFluid?: boolean;
} & Omit<ComponentProps<typeof ScrollView>, 'ref'>;

// ─── Compound ────────────────────────────────────────────────────────────────
// Wrapped in a const due to the `forwardRef` typing of `CustomizationDetailsContainer`.

export const CustomizationDetailsCompoundContainer = {
  Aside: CustomizationDetailsContainerAside,
  ScrollView: CustomizationDetailsContainerScrollView,
};
